import axios from 'axios';
import { getAuthenticationInfo } from '../common/auth';
import { getFullLanguageCode } from '../languages';
import * as logger from '../logging/logger';

const configReader = require('../config');

async function fetchData(endpoint, method, data = {}, language, timeZone, params = null, signal = null) {
  try {
    const { Token } = getAuthenticationInfo() ?? {};
    const { wsApiUrl } = configReader.get();
    const config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
    };

    if (signal) {
      config.signal = signal;
    }

    let bearer = Token;
    const fullEndpoint = `${wsApiUrl}${endpoint}`;

    let response = null;

    config.headers.tz = timeZone;

    if (params) {
      config.params = params;
    }

    if (bearer) {
      config.headers.Authorization = `Bearer ${bearer}`;
    }

    switch (method) {
      case 'get':
        response = await axios.get(fullEndpoint, config);
        break;

      case 'post':
        response = await axios.post(fullEndpoint, data, config);
        break;

      case 'delete':
        response = await axios.delete(fullEndpoint, { ...config, data });
        break;

      case 'patch':
        response = await axios.patch(fullEndpoint, data, config);
        break;

      default:
        throw new Error('fetchData: unknown method');
    }

    return response.data;
  }
  catch (e) {
    if (axios.isCancel && axios.isCancel(e)) {
      return;
    }

    let message = '';
    let error = null;

    if (!e.request) {
      message = 'Client error';
      error = new Error(message);
      error.payload = { message };
      logger.error(message + e);

      throw error;
    }

    if (!e.response) {
      message = 'Unable to contact server';
      error = new Error(message);
      error.payload = { message };

      logger.error(message);

      throw error;
    }

    const statusCode = e.response.status;
    const responseData = e.response.data;
    const requestId = e.response.headers ? e.response.headers.requestid : 'Unknown';

    if (!responseData) {
      message = 'Unable to get response data from server';
      error = new Error(message);
      error.payload = { message, statusCode, requestId };

      logger.error(`${message} ${statusCode} id:${requestId}`);

      throw error;
    }

    const errorMessage = readErrorMessage(responseData);

    message = `Server returned error: ${errorMessage}`;
    error = new Error(message);
    // responseData: endpoints that answer a refusal with a body of their own (e.g. the proxy
    // replacement's 409) put the reason there, not in Exceptions.
    error.payload = { message, statusCode, translatedMessage: errorMessage, responseData };

    logger.error(`${message} ${statusCode} id:${requestId}`);

    throw error;
  }
}

// The reason a refusal carries. `{ Exceptions: [...] }` is the admin's usual shape;
// many admin endpoints answer a 400/404/409 with a plain string instead
// (BadRequest("…")), or with an object of their own: `{ success, message,
// errorMessage }` (areas, redirects), `{ Success, Message }`, `{ error, reason }`
// (non-partner facilities), `{ reason }` (PLAY events). Without this those reached
// the pages as an empty "Server returned error: ".
function readErrorMessage(responseData) {
  if (typeof responseData === 'string') {
    const text = responseData.trim();
    // An HTML error page (proxy, ingress) is not a message.
    return text.startsWith('<') ? '' : text.slice(0, 1000);
  }

  if (typeof responseData !== 'object') {
    return '';
  }

  if (Array.isArray(responseData.Exceptions)) {
    return responseData.Exceptions.join('\n');
  }

  const candidate = ['message', 'Message', 'errorMessage', 'ErrorMessage', 'error', 'reason', 'title']
    .map(key => responseData[key])
    .find(value => typeof value === 'string' && value.trim() !== '');

  return candidate || '';
}

export function requestGet(endpoint, language, timeZone, params, signal = null) {
  return fetchData(endpoint, 'get', null, language, timeZone, params, signal);
}

export function requestPost(endpoint, language, timeZone, data, params, signal = null) {
  return fetchData(endpoint, 'post', data, language, timeZone, params, signal);
}

export function requestPatch(endpoint, language, timeZone, data, params, signal = null) {
  return fetchData(endpoint, 'patch', data, language, timeZone, params, signal);
}

export function requestDelete(endpoint, language, timeZone, data = null, params, signal = null) {
  return fetchData(endpoint, 'delete', data, language, timeZone, params, signal);
}
